/**
 * Pure computation functions for session Gantt generation.
 * No React, no API calls — takes recipe details as input.
 */

import type { RecipeDetail } from "@/client/api";

const GANTT_COLORS = [
  "#f97316",
  "#14b8a6",
  "#3b82f6",
  "#ef4444",
  "#a855f7",
  "#34b561",
  "#e0b92e",
  "#df5a98",
];

/** Builds short prefixes from recipe names (first word, or first two if short). */
export function buildGanttPrefixes(
  recipeIds: string[],
  detailMap: Map<string, RecipeDetail>,
): Map<string, string> {
  const prefixes = new Map<string, string>();
  for (const id of recipeIds) {
    const name = detailMap.get(id)?.name ?? id;
    const words = name.split(/\s+/);
    prefixes.set(
      id,
      words[0]!.length <= 3 && words.length > 1 ? `${words[0]!} ${words[1]!}` : words[0]!,
    );
  }
  return prefixes;
}

/** Collects resource names used across all meal-prep steps. */
export function collectResources(details: RecipeDetail[]): Set<string> {
  const resourceSet = new Set<string>();
  for (const detail of details) {
    for (const step of detail.steps) {
      if (step.phase !== "meal-prep" && step.phase != null) continue;
      for (const r of step.resources) resourceSet.add(r.resource_name);
    }
  }
  return resourceSet;
}

/** Formats a duration in minutes to the Gantt DSL format. */
export function formatDuration(minutes: number): string {
  if (minutes >= 60) {
    return minutes % 60 === 0 ? `${minutes / 60}h` : `${Math.floor(minutes / 60)}h${minutes % 60}m`;
  }
  return `${minutes}m`;
}

/** Builds Gantt task lines from recipe details. Returns the task lines and step-to-taskId map. */
export function buildGanttTaskLines(
  recipeIds: string[],
  detailMap: Map<string, RecipeDetail>,
  prefixes: Map<string, string>,
): string[] {
  const taskLines: string[] = [];
  const stepTaskId = new Map<string, string>();
  let colorIdx = 0;

  for (const recipeId of recipeIds) {
    const detail = detailMap.get(recipeId);
    if (!detail) continue;
    const prefix = prefixes.get(recipeId)!;
    const color = GANTT_COLORS[colorIdx % GANTT_COLORS.length];
    colorIdx++;

    const mealPrepSteps = detail.steps
      .filter((s) => (s.phase ?? "meal-prep") === "meal-prep")
      .sort((a, b) => (a.order_num ?? 0) - (b.order_num ?? 0));

    for (const step of mealPrepSteps) {
      const clean = step.description.replace(/[|,]/g, " ").replace(/\s+/g, " ").trim();
      const desc = clean.length > 40 ? clean.slice(0, 37).trim() + "…" : clean;
      const taskId = `${prefix}-${desc}`;
      stepTaskId.set(step.id, taskId);

      const durStr = formatDuration(step.duration_minutes ?? 10);

      const lanes = [`Persona${step.is_passive ? "(P)" : ""}`];
      for (const r of step.resources) lanes.push(r.resource_name);

      const deps = step.depends_on.map((depId) => stepTaskId.get(depId)).filter(Boolean);
      const depStr = deps.length > 0 ? ` | after ${deps.join(", ")}` : "";

      taskLines.push(`${taskId} | ${durStr} | ${lanes.join(", ")} | ${color}${depStr}`);
    }
  }

  return taskLines;
}

/** Assembles a full Gantt DSL string from lanes and task lines. */
export function assembleGanttString(
  resources: Set<string>,
  taskLines: string[],
  personaCount: number = 2,
  startTime: string = "09:00",
  endTime: string = "15:00",
): string {
  const lanes = [`Persona: ${personaCount}`, ...Array.from(resources)];
  return [
    "VERSION 1",
    "",
    `START ${startTime}`,
    `END ${endTime}`,
    "",
    "LANES",
    ...lanes,
    "",
    "TASKS",
    ...taskLines,
  ].join("\n");
}
